import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export type AlertVariant = "error" | "success" | "info" | "warning";

export interface AlertProps {
  variant?: AlertVariant;
  /** Título opcional en negrita sobre el cuerpo. */
  title?: ReactNode;
  children?: ReactNode;
  /** Si se pasa, aparece la X de cerrar. */
  onDismiss?: () => void;
  /** Oculta el icono (útil dentro de espacios muy estrechos). */
  hideIcon?: boolean;
  className?: string;
}

const variantClasses: Record<AlertVariant, string> = {
  error: "border-ds-terracotta/30 bg-ds-warn-bg/70 text-ds-bad-t",
  success: "border-ds-success/30 bg-ds-success-soft/70 text-[#2b6b4c]",
  info: "border-ds-border bg-ds-mist/70 text-ds-ink-3",
  warning: "border-ds-amber-soft/40 bg-[#FCF5EC]/80 text-ds-terracotta",
};

const variantIcons: Record<AlertVariant, typeof Info> = {
  error: XCircle,
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
};

/**
 * Aviso en línea.
 *
 * Un error usa `role="alert"` + `aria-live="assertive"`: interrumpe al
 * lector de pantalla, que es lo correcto cuando el envío del formulario
 * acaba de fallar. El resto usa `role="status"` + `aria-live="polite"`,
 * que espera a que la persona termine lo que está leyendo.
 */
export function Alert({
  variant = "info",
  title,
  children,
  onDismiss,
  hideIcon = false,
  className,
}: AlertProps) {
  const Icon = variantIcons[variant];
  const isError = variant === "error";

  return (
    <div
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      className={cn(
        "flex items-start gap-2.5 rounded-2xl border px-4 py-3 text-sm backdrop-blur",
        variantClasses[variant],
        className,
      )}
    >
      {!hideIcon && <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}

      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "leading-relaxed")}>{children}</div>}
      </div>

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar aviso"
          className="-mr-1 -mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg opacity-60 transition hover:bg-black/5 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
